import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

const BALL_BASE =
  "https://images.unsplash.com/photo-1769454296931-8e76f5529f68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxnbG93aW5nJTIwY3J5c3RhbCUyMGJhbGwlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODg4MjY3OTd8MA&ixlib=rb-4.1.0&q=85";
const SMOKE =
  "https://images.unsplash.com/photo-1630695230041-8909e3204778?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxtYWdpY2FsJTIwbWlzdCUyMGZvZyUyMGRhcmt8ZW58MHx8fHwxNzg4ODI2Nzk3fDA&ixlib=rb-4.1.0&q=85";

const CrystalBall = ({ phase, revealedImage, shakeKey = 0, gallery, size = "w-[min(74vw,400px)]" }) => {
  const reduced = useReducedMotion();
  const loading = phase === "loading";
  const revealed = phase === "revealed";
  const hasGallery = Array.isArray(gallery) && gallery.length > 0;
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (!hasGallery) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % gallery.length), 5200);
    return () => clearInterval(id);
  }, [hasGallery, gallery]);

  const smokeA = revealed ? 0 : loading ? 1 : hasGallery ? 0.5 : 0.85;
  const smokeB = revealed ? 0 : loading ? 0.9 : hasGallery ? 0.3 : 0.6;

  return (
    <motion.div
      data-testid="crystal-ball"
      key={shakeKey}
      animate={shakeKey > 0 ? { x: [0, -14, 12, -8, 6, 0] } : { x: 0 }}
      transition={{ duration: 0.5 }}
      className={`relative mx-auto transition-[width] duration-1000 ease-out ${size}`}
    >
      {/* Volumetric glow */}
      <motion.div
        aria-hidden="true"
        className="absolute -inset-16 rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(139,92,246,0.35) 0%, rgba(245,158,11,0.08) 55%, transparent 75%)",
        }}
        animate={{ opacity: loading ? [0.85, 1, 0.85] : revealed ? 1 : 0.7 }}
        transition={loading ? { repeat: Infinity, duration: 1.6 } : { duration: 1.2 }}
      />

      {/* Sphere */}
      <div className="relative aspect-square w-full overflow-hidden rounded-full border border-white/10 shadow-[0_30px_80px_-20px_rgba(3,0,20,0.9),inset_0_0_60px_rgba(3,0,20,0.55)]">
        <img
          src={BALL_BASE}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />

        {/* Gallery of visions (welcome page) */}
        {hasGallery && (
          <AnimatePresence>
            <motion.img
              key={slide}
              src={gallery[slide % gallery.length]}
              alt=""
              aria-hidden="true"
              data-testid="gallery-vision"
              className="absolute inset-0 h-full w-full object-cover"
              initial={{ opacity: 0, scale: 1.12, filter: "blur(10px)" }}
              animate={{ opacity: 0.9, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 1.06, filter: "blur(8px)" }}
              transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
            />
          </AnimatePresence>
        )}

        {/* Smoke layers */}
        <motion.div
          aria-hidden="true"
          className="absolute -inset-[30%] mix-blend-screen"
          style={{
            backgroundImage: `url(${SMOKE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          animate={{
            rotate: reduced ? 0 : 360,
            opacity: smokeA,
          }}
          transition={{
            rotate: { repeat: Infinity, ease: "linear", duration: loading ? 5 : 46 },
            opacity: { duration: revealed ? 1.6 : 0.8 },
          }}
        />
        <motion.div
          aria-hidden="true"
          className="absolute -inset-[30%] mix-blend-screen"
          style={{
            backgroundImage: `url(${SMOKE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transform: "scaleX(-1)",
            filter: "hue-rotate(35deg)",
          }}
          animate={{
            rotate: reduced ? 0 : -360,
            opacity: smokeB,
          }}
          transition={{
            rotate: { repeat: Infinity, ease: "linear", duration: loading ? 7 : 64 },
            opacity: { duration: revealed ? 1.6 : 0.8 },
          }}
        />

        {/* Revealed vision */}
        {revealedImage && (
          <motion.img
            src={revealedImage}
            alt="Vision révélée par l'oracle"
            data-testid="oracle-image"
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ opacity: 0, scale: 1.18, filter: "blur(14px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          />
        )}

        {/* Glass reflections */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 30% 22%, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.06) 26%, transparent 46%)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{ boxShadow: "inset 0 -30px 60px rgba(3,0,20,0.65), inset 0 0 0 1px rgba(255,255,255,0.06)" }}
        />
      </div>

      {/* Pedestal */}
      <div aria-hidden="true" className="mx-auto mt-[-14px] h-10 w-2/3">
        <div
          className="h-full w-full"
          style={{
            background: "radial-gradient(ellipse at center, rgba(245,158,11,0.22) 0%, transparent 70%)",
            filter: "blur(6px)",
          }}
        />
      </div>
      <div
        aria-hidden="true"
        className="mx-auto h-[3px] w-1/2 rounded-full bg-gradient-to-r from-transparent via-amber-500/40 to-transparent"
      />
    </motion.div>
  );
};

export default CrystalBall;
